import React, { useState, useEffect, Fragment } from "react";
import { Accordion, AccordionDetails, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Api's
import { quoteHistoricalApi } from "@/main/api";

// Utils
import { getTranslateByKey, isEmpty, isNullUndefined, isNullUndefinedEmpty } from "@/main/services/utils";
import { formatCurrency, formatPercentage } from "@/main/services/utilsNumber";
import { userIsSupplier } from "@/main/services/utilsUser";
import { QuoteHistoricalActionEnum } from "@/main/services/enum/QuoteHistoricalActionEnum";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";

// Models
import {
	FieldsChangedQuoteHistorical,
	IPartsQuoteHistory,
	ImageQuoteHistoryResponse,
	QuoteHistoricalMainResponse,
	QuoteHistoricalResponse,
} from "@/main/models/response/quoteHistoricalResponse";

// Components
import TypographyPriceCap from "@/main/components/common/typography/typographyPriceCap";
import QuoteProcurementPartInfo from "@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo";
import PriceDiffPercent from "../../priceDiffPercent/priceDiffPercent";
import KOGPaginationGrid from "../../paginationGrid/paginationGrid";

// Styles
import * as S from "./styles";
import LoadingComponent from "../../loading/loadingComponent";

interface IModalQuoteHistoryProps {
	guidQuote: string | null;
	showPrices: boolean;
	guidScreen?: string;
}
export function ModalQuoteHistory({ guidQuote, showPrices, guidScreen }: IModalQuoteHistoryProps) {
	// Constants
	const ID_SCROLLABLE_TARGET = "id-scrollable-target-historyquote";

	// States
	const [formFilters, setFormFilters] = useState<any | null>(null);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [dataGrid, setDataGrid] = useState<QuoteHistoricalMainResponse[]>([]);

	const historyHasDetail = (idQuoteHistoricalAction: number) => {
		const idsActionWithDetail: number[] = [
			QuoteHistoricalActionEnum.Edited,
			QuoteHistoricalActionEnum.Requoted,
			QuoteHistoricalActionEnum.OfferRequested,
			QuoteHistoricalActionEnum.PartCanceled,
			QuoteHistoricalActionEnum.PartRequoted,
			QuoteHistoricalActionEnum.PartAnswered,
			QuoteHistoricalActionEnum.PartAnsweredManually,
			QuoteHistoricalActionEnum.PartAddedToCart,
			QuoteHistoricalActionEnum.PartRemovedFromCart,
			QuoteHistoricalActionEnum.ImageInserted,
			QuoteHistoricalActionEnum.Manualcorrected,
			QuoteHistoricalActionEnum.ChangeStatusManualQuote,
			QuoteHistoricalActionEnum.ChangeStatus,
			QuoteHistoricalActionEnum.ImageDeleted,
			QuoteHistoricalActionEnum.CreatedByIntegration,
			QuoteHistoricalActionEnum.CreatedIntegrationJson,
			QuoteHistoricalActionEnum.CreatedIntegrationPdf,
			QuoteHistoricalActionEnum.CreatedIntegrationXlsx,
			QuoteHistoricalActionEnum.CreatedByrequote,
			QuoteHistoricalActionEnum.CreatedByrequoteNational,
			QuoteHistoricalActionEnum.PartGlass,
			QuoteHistoricalActionEnum.PartTire,
			QuoteHistoricalActionEnum.PartAnsweredManuallyByBuyer,
			QuoteHistoricalActionEnum.NoSupplierFound,
			QuoteHistoricalActionEnum.PartsAnsweredByIntegration,
			QuoteHistoricalActionEnum.PartMovedToApproved,
			QuoteHistoricalActionEnum.PartMovedToRejected,
			QuoteHistoricalActionEnum.UnavaiblePartMovedToRejected,
			QuoteHistoricalActionEnum.AttachmentInserted,
			QuoteHistoricalActionEnum.PartPriceCorrected,
		];

		return idsActionWithDetail.includes(idQuoteHistoricalAction);
	};

	function onResetSearch() {
		if (isNullUndefined(formFilters)) return;

		setDataGrid([]);
		setFormFilters(null);
	}

	async function getDataHistory(filters: any = {}, isResetSearch: boolean = true) {
		const request: any = {
			...(isResetSearch ? {} : formFilters),
			...(filters as any),
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize10,
		};
		onResetSearch();
		setFormFilters(request);
	}

	async function fetchDataHistoryData(request: any) {
		return quoteHistoricalApi.getQuoteHistory(request, String(guidQuote)).then((response) => response.data);
	}

	useEffect(() => {
		getDataHistory();
	}, []);

	return (
		<S.Content>
			<S.ContainerBodyHistory id={ID_SCROLLABLE_TARGET}>
				<KOGPaginationGrid
					idScrollableTarget={ID_SCROLLABLE_TARGET}
					showTitleResults={false}
					totalRecords={totalRecords}
					setTotalRecords={setTotalRecords}
					dataGrid={dataGrid}
					formFilters={formFilters}
					setDataGrid={setDataGrid}
					fetchMore={fetchDataHistoryData}
				>
					<S.Container>
						{dataGrid.map((item, index) => {
							if (historyHasDetail(item.idQuoteHistoricalAction)) {
								return (
									<QuoteHistoryDetail
										key={item.guidQuoteHistorical}
										showPrices={showPrices}
										guidHistorical={item.guidQuoteHistorical}
										guidQuote={String(guidQuote)}
										guidScreen={guidScreen}
										mainContent={item}
									/>
								);
							} else {
								return (
									<S.ContainerBox key={index}>
										<Typography variant="body2">
											<span>
												<strong>{item.quoteHistoricalAction}</strong>
											</span>
											{` - ${item.date} - ${item.hour} - ${item.userName}`}
										</Typography>
									</S.ContainerBox>
								);
							}
						})}
					</S.Container>
				</KOGPaginationGrid>
			</S.ContainerBodyHistory>
		</S.Content>
	);
}

function RenderValueEdited({ field, from, to }: FieldsChangedQuoteHistorical) {
	return (
		<S.BoxAccordionDetails>
			<Typography>
				<strong>{field}</strong>
			</Typography>
			<Typography>
				<strong>{`${getTranslateByKey("information.from")}: `}</strong>
				{from}
			</Typography>
			<Typography>
				<strong>{`${getTranslateByKey("information.to")}: `}</strong>
				{to}
			</Typography>
		</S.BoxAccordionDetails>
	);
}

interface RenderLabelLinkProps extends ImageQuoteHistoryResponse {
	labelTranslationKey: string;
}

function RenderLabelLink({ link, name, labelTranslationKey }: RenderLabelLinkProps) {
	return (
		<div>
			{getTranslateByKey(labelTranslationKey)}:&nbsp;
			<S.LinkHistory href={link} target="_blank">
				{name}
			</S.LinkHistory>
		</div>
	);
}

interface QuoteHistoryDetailProps {
	mainContent: QuoteHistoricalMainResponse;
	guidHistorical: string;
	showPrices: boolean;
	guidQuote: string;
	guidScreen?: string | null;
}

function QuoteHistoryDetail({
	mainContent,
	guidHistorical,
	showPrices,
	guidQuote,
	guidScreen,
}: QuoteHistoryDetailProps) {
	const [detail, setDetail] = useState<QuoteHistoricalResponse | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [detailApiIsAlreadyCalled, setDetailApiIsAlreadyCalled] = useState<boolean>(false);
	const imageOrFileLabelTranslationKey =
		mainContent.idQuoteHistoricalAction === QuoteHistoricalActionEnum.AttachmentInserted
			? "information.file"
			: "quoteCard.image";

	function renderResult() {
		if (isLoading) return <LoadingComponent />;

		if (isNullUndefined(detail)) return <></>;
		if (!isNullUndefined(detail) && !isLoading) {
			return (
				<AccordionDetails>
					<S.ContentAccordionDetails>
						{detail.previousStatus && (
							<Fragment>
								<strong>
									{detail.previousStatus}
									&nbsp;&rarr;&nbsp;
									{detail.currentStatus}
									{detail.reasonName !== "" ? ` (${detail.reasonName})` : ""}
								</strong>
							</Fragment>
						)}
						{detail.fieldsChanged?.map((item) => (
							<RenderValueEdited key={item.field} field={item.field} to={item.to} from={item.from} />
						))}
						{!isNullUndefined(detail.imageQuoteHistory) &&
							detail.imageQuoteHistory.length > 0 &&
							detail.imageQuoteHistory?.map(({ link, name }: ImageQuoteHistoryResponse) => (
								<RenderLabelLink
									key={link}
									link={link}
									name={name}
									labelTranslationKey={imageOrFileLabelTranslationKey}
								/>
							))}
						{detail.parts && detail.parts.length != 0 && (
							<S.ContentPartsList>
								{detail.parts?.map((part) => (
									<RenderPartsHistory
										key={part.partGuid || Math.random()}
										id={mainContent.idQuoteHistoricalAction}
										part={part}
										showPrices={showPrices}
									/>
								))}
							</S.ContentPartsList>
						)}
						{mainContent.idQuoteHistoricalAction === QuoteHistoricalActionEnum.PartPriceCorrected &&
							!isNullUndefinedEmpty(detail.reasonName) && (
								<Typography>
									<strong>{`${getTranslateByKey("information.reason")}: `}</strong>
									{detail.reasonName}
									{!isNullUndefinedEmpty(detail.reason) && ` (${detail.reason})`}
								</Typography>
							)}
						{detail.pricingDiscrepancy && detail.pricingDiscrepancy.length != 0 && (
							<S.ContentPartsList>
								<strong>{getTranslateByKey("reason.answered")}</strong>
								{detail.pricingDiscrepancy?.map((answered) => (
									<RenderPartsHistory
										key={answered.partGuid || Math.random()}
										id={mainContent.idQuoteHistoricalAction}
										part={answered}
										showPrices={showPrices}
									/>
								))}
							</S.ContentPartsList>
						)}
						{detail.partsGenerateOrder && detail.partsGenerateOrder.length != 0 && (
							<S.ContentPartsList>
								<strong>{getTranslateByKey("reason.answeredOrder")}</strong>
								{detail.partsGenerateOrder?.map((answeredOrder) => (
									<RenderPartsHistory
										key={answeredOrder.partGuid || Math.random()}
										id={mainContent.idQuoteHistoricalAction}
										part={answeredOrder}
										showPrices={showPrices}
									/>
								))}
							</S.ContentPartsList>
						)}
						{detail.noSuppliersAnswer && detail.noSuppliersAnswer.length != 0 && (
							<S.ContentPartsList>
								<strong>{getTranslateByKey("reason.noAnswered")}</strong>
								{detail.noSuppliersAnswer?.map((noAnswered) => (
									<RenderPartsHistory
										key={noAnswered.partGuid || Math.random()}
										id={mainContent.idQuoteHistoricalAction}
										part={noAnswered}
										showPrices={showPrices}
									/>
								))}
							</S.ContentPartsList>
						)}
						<S.BoxDetails>
							{!isNullUndefined(detail.repairCost) && (
								<Typography variant="body1">
									<span>
										<strong>{`${getTranslateByKey("information.repairCost")}: `}</strong>
										{formatCurrency(detail.repairCost)}
									</span>
								</Typography>
							)}
							{detail.partCostSum && (
								<Typography variant="body1">
									<span>
										<strong>{`${getTranslateByKey("information.sumOfAllPartsPrice")}: `}</strong>
										{formatCurrency(detail.partCostSum)}
									</span>
								</Typography>
							)}
							{detail.deductible && (
								<Typography variant="body1">
									<span>
										<strong>{`${getTranslateByKey("information.deductible")}: `}</strong>
										{formatCurrency(detail.deductible)}
									</span>
								</Typography>
							)}
							{!isNullUndefined(detail.deductiblePercentage) && (
								<Typography variant="body1">
									<span>
										<strong>{`${getTranslateByKey("information.percentageOfDeductibleMargin")}: `}</strong>
										{formatPercentage(detail.deductiblePercentage)}
									</span>
								</Typography>
							)}
							{detail.vehicleValue && (
								<Typography variant="body1">
									<span>
										<strong>{`${getTranslateByKey("information.vehicleValue")}: `}</strong>
										{formatCurrency(detail.vehicleValue)}
									</span>
								</Typography>
							)}
							{detail.totalLossRate && (
								<Typography variant="body1">
									<span>
										<strong>{`${getTranslateByKey("information.totalLossRate")}: `}</strong>
										{formatPercentage(detail.totalLossRate)}
									</span>
								</Typography>
							)}
						</S.BoxDetails>
					</S.ContentAccordionDetails>
				</AccordionDetails>
			);
		}
	}

	return (
		<Accordion
			onChange={(_: React.SyntheticEvent, expanded: boolean) => {
				if (expanded && !detailApiIsAlreadyCalled) {
					setDetailApiIsAlreadyCalled(true);
					setIsLoading(true);
					quoteHistoricalApi
						.getQuoteHistoryDetail({
							guidQuote: String(guidQuote),
							guidScreen: !isNullUndefined(guidScreen) ? guidScreen : null,
							guid: guidHistorical,
						})
						.then((response) => {
							const hasResultApiDetail = !isNullUndefined(response.data) && !isNullUndefined(response.data.result);

							if (hasResultApiDetail) setDetail(response.data.result);
						})
						.catch(() => setDetailApiIsAlreadyCalled(false))
						.finally(() => setIsLoading(false));
				}
			}}
		>
			<S.ContainerAccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Typography variant="body2">
					<span>
						<strong>{mainContent.quoteHistoricalAction}</strong>
					</span>
					{` - ${mainContent.date} - ${mainContent.hour} - ${mainContent.userName}`}
				</Typography>
			</S.ContainerAccordionSummary>
			{renderResult()}
		</Accordion>
	);
}

interface IRenderPartsHistory {
	part: IPartsQuoteHistory;
	id: number;
	showPrices: boolean;
}

function RenderPartsHistory({ part, id, showPrices }: IRenderPartsHistory) {
	const isQuoteHistoricalActionOfferRequested = () =>
		id === QuoteHistoricalActionEnum.OfferRequested || id === QuoteHistoricalActionEnum.Requoted;
	const isQuoteHistoricalActionChangeStatusManualQuote = () => id === QuoteHistoricalActionEnum.ChangeStatusManualQuote;
	const isDirectAssign = () =>
		id === QuoteHistoricalActionEnum.PartAnsweredManuallyByBuyer ||
		id === QuoteHistoricalActionEnum.PartsAnsweredByIntegration;
	const isPartMovedToApprovedOrRejected = () =>
		id === QuoteHistoricalActionEnum.PartMovedToApproved ||
		id === QuoteHistoricalActionEnum.PartMovedToRejected ||
		id === QuoteHistoricalActionEnum.UnavaiblePartMovedToRejected;
	const isPartPriceCorrected = () => id === QuoteHistoricalActionEnum.PartPriceCorrected;
	const hasPrice = !isNullUndefined(part.price) && part.price > 0;

	const isUserSupplier = userIsSupplier();
	const renderInfoPartWeightOrPackageType = () =>
		((!isNullUndefined(part.partWeight) && part.partWeight > 0) || !isNullUndefined(part.packageType)) &&
		!part.deliveryBySupplier;

	const renderInfoPartDimensions = () =>
		!isNullUndefined(part.partHeight) &&
		part.partHeight > 0 &&
		!isNullUndefined(part.partWidth) &&
		part.partWidth > 0 &&
		!isNullUndefined(part.partDepth) &&
		part.partDepth > 0 &&
		!part.deliveryBySupplier;

	function renderReason() {
		if (isNullUndefined(part.reason) || isDirectAssign()) return null;

		if (isPartMovedToApprovedOrRejected()) {
			return (
				<Typography>
					{part.reason}
					{!isNullUndefined(part.reasonDescription) && ` (${part.reasonDescription})`}
				</Typography>
			);
		}

		return (
			<Typography>
				{getTranslateByKey("information.notAvailable")}
				{!isNullUndefined(part.reason) && ` > ${part.reason}`}
				{!isNullUndefined(part.reasonDescription) && ` (${part.reasonDescription})`}
			</Typography>
		);
	}

	function renderReasonDirectAssign() {
		if (isNullUndefined(part.reason) || !isDirectAssign()) return null;

		return (
			<p>
				<span>
					<strong>{getTranslateByKey("information.reason")}: </strong>
				</span>
				{part.reason} {part.reasonDescription && `(${part.reasonDescription})`}
			</p>
		);
	}

	function renderPriceCorrection() {
		if (isNullUndefined(part.previousPrice)) return null;

		return (
			<S.AreaPrice>
				<Typography>
					<strong>{`${getTranslateByKey("information.from")}: `}</strong>
					{formatCurrency(part.previousPrice)}
				</Typography>
				<Typography>
					<strong>{`${getTranslateByKey("information.to")}: `}</strong>
					{formatCurrency(part.price)}
				</Typography>
			</S.AreaPrice>
		);
	}

	function renderPartHistoryWithPrice() {
		return (
			<>
				{/* Supplier info */}
				{!isQuoteHistoricalActionChangeStatusManualQuote() && !isQuoteHistoricalActionOfferRequested() && (
					<Typography>
						<strong>{part.supplier}</strong>
						{!isNullUndefined(part.sku) ? (
							<strong style={{ marginLeft: 5 }}>{!isEmpty(part.sku) && `- ${part.sku}`}</strong>
						) : (
							<></>
						)}
					</Typography>
				)}

				{/* Part information */}
				<QuoteProcurementPartInfo
					data={part.quoteProcurementPartInfo}
					renderType="column"
					showLineSupplierInfo={{ info: "partType" }}
				/>

				{/* Reason */}
				{renderReason()}

				{/* Price, PackageType, PartWeight, PartHeight, PartWidth, PartDepth  */}
				{!isQuoteHistoricalActionChangeStatusManualQuote() && !isQuoteHistoricalActionOfferRequested() && (
					<>
						{showPrices && isPartPriceCorrected() && renderPriceCorrection()}
						{showPrices && !isPartPriceCorrected() && !isEmpty(part.price) && (
							<S.AreaPrice>
								<Typography>
									{formatCurrency(part.price)}
									{!isUserSupplier && !isNullUndefined(part.priceDiffPercent) && (
										<PriceDiffPercent diffPercent={part.priceDiffPercent} />
									)}
								</Typography>
								{!isUserSupplier &&
									(isNullUndefined(part.systemReferencePrice) ? (
										!isNullUndefined(part.priceCap) && <TypographyPriceCap priceCap={formatCurrency(part.priceCap)} />
									) : (
										<p style={{ margin: "0" }} translate="no">
											{getTranslateByKey("information.systemReferencePrice")}:{" "}
											{formatCurrency(part.systemReferencePrice)}
										</p>
									))}
							</S.AreaPrice>
						)}

						{renderInfoPartWeightOrPackageType() && (
							<Typography>
								{!isNullUndefined(part.partWeight) && part.partWeight > 0 ? `${part.partWeight} kg` : ""}
								&nbsp;
								{!isNullUndefined(part.packageType) ? `- ${part.packageType}` : ""}
							</Typography>
						)}
						{renderInfoPartDimensions() && (
							<Typography>
								{part.partHeight} x {part.partWidth} x {part.partDepth} cm
							</Typography>
						)}
					</>
				)}
				{renderReasonDirectAssign()}
			</>
		);
	}

	return (
		<S.Container>
			{hasPrice ? (
				renderPartHistoryWithPrice()
			) : (
				<>
					{/* Supplier info */}
					{!isQuoteHistoricalActionChangeStatusManualQuote() && !isQuoteHistoricalActionOfferRequested() && (
						<Typography>
							<strong>{part.supplier}</strong>
						</Typography>
					)}

					{/* Part information */}
					<QuoteProcurementPartInfo
						data={part.quoteProcurementPartInfo}
						renderType="column"
						showLineSupplierInfo={{ info: "partType" }}
					/>

					{/* Reason */}
					{renderReason()}
				</>
			)}
		</S.Container>
	);
}

export default ModalQuoteHistory;
